import React, { useMemo, useState, useEffect, useCallback } from "react";
import BreadCrumb from '../../../Common/BreadCrumb'
import {Card, Col, Container, Form, Row, Button} from 'react-bootstrap'
import {useTranslation} from "react-i18next";
import {mockScenarios} from "../../../data/scenarios";
import {Link} from "react-router-dom";
import DataTable from "../../../Common/DataTable";
import {COLORS} from "../../../Common/constants/colors";
import {dashboardAPI} from "../../../services/api";



const ScenarioList = () => {
    const {t} = useTranslation();

    document.title=t('ScenarioList.title');

    // Check if user is demo user
    const isDemoUser = useMemo(() => {
        const authUser = sessionStorage.getItem("authUser");
        if (!authUser) return false;
        try {
            const userData = JSON.parse(authUser);
            const token = userData.token || userData.access_token;
            return token?.startsWith('demo-token-') || userData.email === 'admin@demo.com.tr';
        } catch {
            return false;
        }
    }, []);

    const [data, setData] = useState<Scenario[]>(mockScenarios);
    const [isLoading, setIsLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    // Handle window resize for mobile detection
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Fetch scenarios from backend for real users
    useEffect(() => {
        if (isDemoUser) {
            // Use mock data for demo users
            setData(mockScenarios);
            return;
        }

        const fetchScenarios = async () => {
            setIsLoading(true);
            try {
                console.log('📋 Fetching scenarios from backend...');
                const response: any = await dashboardAPI.getScenarios();
                
                // Normalize response - handle both AxiosResponse and direct data
                const scenarios = Array.isArray(response) ? response : (response?.data || []);
                
                console.log('✅ Scenarios received:', scenarios.length, 'scenarios');
                
                // Map backend ScenarioDTO to frontend Scenario format
                const mappedScenarios: Scenario[] = scenarios.map((scenario: any) => {
                    // Parse conditionTreeJson to extract conditions
                    let conditions: ScenarioConditions[] = [];
                    if (scenario.conditionTreeJson) {
                        try {
                            const conditionTree = JSON.parse(scenario.conditionTreeJson);
                            conditions = extractConditionsFromTree(conditionTree);
                        } catch (e) {
                            console.warn('⚠️ Failed to parse conditionTreeJson:', e);
                        }
                    }
                    
                    // Format date (backend returns LocalDateTime, convert to string)
                    const createdOn = scenario.createdOn 
                        ? (typeof scenario.createdOn === 'string' 
                            ? scenario.createdOn.split('T')[0] 
                            : new Date(scenario.createdOn).toISOString().split('T')[0])
                        : new Date().toISOString().split('T')[0];
                    
                    return {
                        id: scenario.id || '',
                        name: scenario.name || '',
                        active: scenario.active !== false,
                        createdOn: createdOn,
                        description: scenario.description || '',
                        conditions: conditions
                    };
                });
                
                setData(mappedScenarios);
            } catch (error: any) {
                console.error('❌ Failed to fetch scenarios:', error);
                // Fall back to empty array on error
                setData([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchScenarios();
    }, [isDemoUser]);

    // Helper function to extract conditions from condition tree
    const extractConditionsFromTree = (node: any): ScenarioConditions[] => {
        if (!node) return [];
        
        const conditions: ScenarioConditions[] = [];
        
        if (node.type === 'CONDITION' && node.condition) {
            // Single condition node
            conditions.push({
                field: node.condition.field || '',
                operator: node.condition.operator || '',
                value: node.condition.value || ''
            });
        } else if (node.conditions && Array.isArray(node.conditions)) {
            // Logical node (AND/OR) - recursively extract from children
            node.conditions.forEach((child: any) => {
                conditions.push(...extractConditionsFromTree(child));
            });
        }
        
        return conditions;
    };

    const handleToggleActive = useCallback((scenario: Scenario, newActive: boolean) => {
        // Only allow toggle for demo users
        if (!isDemoUser) return;
        
        setData(prevData => {
            const newData = [...prevData];
            const index = newData.findIndex(s => s.id === scenario.id);
            if (index !== -1) {
                newData[index].active = newActive;
            }
            return newData;
        });
    }, [isDemoUser]);

    const columns = useMemo(
        () => {
            const cols: any[] = [
                {
                    Header: t('ScenarioList.scenario_name'),
                    accessor: "name",
                    Cell: ({ row, value }: any) => {
                        const scenario = data[row.index];
                        return (
                            <div style={{minWidth: 0}}>
                                <Link 
                                    to={`/scenario/${scenario.id}`} 
                                    style={{ color: COLORS.LINK, textDecoration: "none", wordBreak: 'break-word' }}
                                >
                                    {value}
                                </Link>
                                {isMobile && (
                                    <div className="mt-1">
                                        <small className="text-muted">ID: {scenario.id}</small>
                                    </div>
                                )}
                            </div>
                        );
                    }
                },
            ];
            
            // Add scenario_id only on desktop
            if (!isMobile) {
                cols.unshift({
                    Header: t('ScenarioList.scenario_id'),
                    accessor: "id",
                    Cell: ({ value }: any) => (
                        <Link to={`/scenario/${value}`} style={{ color: COLORS.LINK, textDecoration: "none" }}>
                            {value}
                        </Link>
                    ),
                });
            }
            
            cols.push(
                {
                    Header: t('ScenarioList.active'),
                    accessor: "active",
                    Cell: ({ row, value }: any) => {
                        const scenario = data[row.index];
                        return (
                            <div className="form-check form-switch form-switch-custom form-switch-primary">
                                <Form.Check 
                                    onChange={() => handleToggleActive(scenario, !value)} 
                                    type="checkbox" 
                                    role="switch" 
                                    id={`SwitchCheck-${row.index}`} 
                                    checked={value}
                                    disabled={!isDemoUser}
                                />
                            </div>
                        );
                    },
                }
            );
            
            // Add created_on only on desktop
            if (!isMobile) {
                cols.push({
                    Header: t('ScenarioList.created_on'),
                    accessor: "createdOn",
                });
            }
            
            return cols;
        },
        [data, t, isDemoUser, isMobile, handleToggleActive]
    );

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb
                        items={[
                            { label: t('Dashboard.header'), path: "/" },
                            { label: t('ScenarioList.page_title'), path: "/scenarios" }
                        ]}
                    />
                    <Row>
                        <Col xl={12}>
                            <Card>
                                <Card.Body>
                                    {isLoading ? (
                                        <div className="text-center py-4">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </div>
                                    ) : isMobile ? (
                                        // Mobile card view with pagination
                                        <>
                                            <div className="d-flex flex-column gap-3">
                                                {data.length === 0 ? (
                                                    <div className="text-center py-5 text-muted">
                                                        {t('ScenarioList.no_scenarios') || 'No scenarios found'}
                                                    </div>
                                                ) : (
                                                    data
                                                        .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                                                        .map((scenario) => (
                                                    <Card key={scenario.id} className="shadow-sm">
                                                        <Card.Body>
                                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                                <div className="flex-grow-1" style={{minWidth: 0}}>
                                                                    <Link 
                                                                        to={`/scenario/${scenario.id}`} 
                                                                        style={{ color: COLORS.LINK, textDecoration: "none" }}
                                                                    >
                                                                        <h6 className="mb-1" style={{wordBreak: 'break-word'}}>
                                                                            {scenario.name}
                                                                        </h6>
                                                                    </Link>
                                                                    <small className="text-muted d-block mb-2">
                                                                        ID: {scenario.id}
                                                                    </small>
                                                                    {scenario.description && (
                                                                        <p className="text-muted mb-2" style={{wordBreak: 'break-word', fontSize: '0.875rem'}}>
                                                                            {scenario.description}
                                                                        </p>
                                                                    )}
                                                                    <div className="d-flex flex-wrap gap-2 align-items-center mt-2">
                                                                        <div className="form-check form-switch form-switch-custom form-switch-primary">
                                                                            <Form.Check 
                                                                                onChange={() => handleToggleActive(scenario, !scenario.active)} 
                                                                                type="checkbox" 
                                                                                role="switch" 
                                                                                id={`scenario-switch-mobile-${scenario.id}`} 
                                                                                checked={scenario.active}
                                                                                disabled={!isDemoUser}
                                                                                label={<small>{t('ScenarioList.active') || 'Active'}</small>}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    {scenario.createdOn && (
                                                                        <small className="text-muted d-block mt-2">
                                                                            📅 {scenario.createdOn}
                                                                        </small>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </Card.Body>
                                                    </Card>
                                                        ))
                                                )}
                                            </div>
                                            {data.length > pageSize && (
                                                <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                                                    <div className="text-muted small">
                                                        {t('ScenarioList.showing') || 'Showing'} {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, data.length)} {t('ScenarioList.of') || 'of'} {data.length}
                                                    </div>
                                                    <div className="d-flex gap-2">
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                                            disabled={currentPage === 1}
                                                        >
                                                            {t('ScenarioList.previous') || 'Previous'}
                                                        </Button>
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() => setCurrentPage(prev => Math.min(Math.ceil(data.length / pageSize), prev + 1))}
                                                            disabled={currentPage >= Math.ceil(data.length / pageSize)}
                                                        >
                                                            {t('ScenarioList.next') || 'Next'}
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        // Desktop table view
                                        <div className="table-responsive">
                                            <DataTable
                                                columns={columns}
                                                data={data}
                                                title={t('ScenarioList.search_scenario')}
                                                editable={false}
                                                alertsDeletable={false}
                                                deletable={false}
                                            />
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default ScenarioList;
